import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { api } from "../../api/client";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { StatusBadge } from "../../components/Badge";
import { useToast } from "../../components/Toast";
import { formatWholeMoney } from "../../lib/money";
import type { Company, CompanyImpact } from "../../api/types";

// 真的刪掉一家客戶(業主 2026-09-20)。
//
// > 客戶那邊也要有刪除 現在雖然是停用 **怕會一起改到之前的報價單**
// > 刪除時跳出來會牽連到哪一些報價單 確定是否刪除
// > 哪一些報價單要可以點進去 …… 如果沒有牽連到就可以直接刪 有牽連到等確認
//
// ==========================================================================
// 他的顧慮有具體的機制,而且已經處理掉了
// ==========================================================================
// `quotes.client_company_id` 是 `ON DELETE SET NULL`,而單子上印的公司名稱是
// 「單子自己的覆寫 → 沒有才抓客戶檔」。所以直接刪下去,舊單的乙方會變空白。
// **後端在刪之前會先把公司名字寫進那幾張單**(見 routers/companies.py),
// 所以印出來一個字都不會變 —— 這一頁要把這件事講給他聽,不然他不敢按。
//
// ==========================================================================
// 沒牽連就不要加儀式
// ==========================================================================
// 業主原話:「如果沒有牽連到就可以直接刪」。沒有東西要看的時候還逼人勾一個
// 「我已經看過」,那個勾選框就退化成一道人人閉著眼睛點過去的手續 ——
// 而它本來的作用正是讓人停下來看。

export interface CompanyDeleteDialogProps {
  /** 有值就是開著,而且是要刪這一家。 */
  company: Company | null;
  onClose: () => void;
}

/** 開新分頁看那張單。
 *
 * **走 `<Link target="_blank">` 而不是原生的 `<a>`。** 整個站掛在 `/quote/`
 * 底下(`main.tsx` 的 basename),`<Link>` 會自己補上那一段,原生的 `<a>` 不會
 * —— 少了它新分頁打開的是一句 `{"detail":"Not Found"}`(業主 2026-09-21 回報
 * 的就是同一個毛病)。`target="_blank"` 在 `<Link>` 上照樣有效:它本來就渲染成
 * 一個真的 `<a>`。 */
function QuoteLink({ id, quoteNo }: { id: number; quoteNo: string }) {
  return (
    <Link
      to={`/quotes/${id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="tabular text-[var(--accent)] underline-offset-2 hover:underline"
    >
      {quoteNo}
    </Link>
  );
}

export function CompanyDeleteDialog({ company, onClose }: CompanyDeleteDialogProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [checked, setChecked] = useState(false);
  const [failed, setFailed] = useState("");

  const open = company !== null;

  // 每次重開都從頭來 —— 上一家勾過的框不可以留給下一家。
  useEffect(() => {
    if (!open) return;
    setChecked(false);
    setFailed("");
  }, [open, company?.id]);

  const { data: impact, isLoading } = useQuery({
    queryKey: ["company-impact", company?.id],
    queryFn: () => api.get<CompanyImpact>(`/companies/${company!.id}/impact`),
    enabled: open,
  });

  const quotes = impact?.quotes ?? [];
  const remove = useMutation({
    mutationFn: () =>
      api.del(`/companies/${company!.id}/permanently`, { quote_count: quotes.length }),
    onSuccess: () => {
      toast.push(`客戶「${company!.name}」已經刪掉了`, "success");
      void queryClient.invalidateQueries({ queryKey: ["companies"] });
      onClose();
    },
    onError: (err: unknown) => {
      // **對話框不關。** 最常見的失敗是「張數變了」,而他要做的事是重看一次,
      // 不是從客戶清單重來一遍。順手把牽連重查,畫面上就是新的那一批。
      setFailed(err instanceof Error ? err.message : "刪不掉");
      setChecked(false);
      void queryClient.invalidateQueries({ queryKey: ["company-impact", company?.id] });
    },
  });

  if (!company) return null;

  const clean = !isLoading && quotes.length === 0;
  const ready = clean || checked;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`刪除客戶「${company.name}」`}
      maxWidth="max-w-3xl"
      // 破壞性的確認不給「點背景就關」—— 手滑點到旁邊就得從頭來一次。
      closeOnBackdrop={false}
      footer={
        <>
          <Button type="button" onClick={onClose}>
            取消
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={!ready}
            loading={remove.isPending}
            onClick={() => remove.mutate()}
            icon={<Trash2 size={15} aria-hidden="true" />}
          >
            確定刪除
          </Button>
        </>
      }
    >
      {isLoading ? <p className="m-0 text-sm text-[var(--text-3)]">查牽連中…</p> : null}

      {clean ? (
        <p className="m-0 text-sm text-[var(--text)]">
          這家客戶<strong>沒有牽連到任何報價單</strong>。刪掉就真的沒了,救不回來。
        </p>
      ) : null}

      {quotes.length > 0 ? (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm text-[var(--text)]">
            下面這 {quotes.length} 張報價單指著「{company.name}」。
            <strong>點單號可以另開分頁看是哪一張</strong>,看完回來這個視窗還在。
          </p>

          <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)]">
            <table aria-label="牽連到的報價單" className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                  <th scope="col" className="px-3 py-2">單號</th>
                  <th scope="col" className="px-3 py-2">標題</th>
                  <th scope="col" className="px-3 py-2">狀態</th>
                  <th scope="col" className="px-3 py-2">報價日期</th>
                  <th scope="col" className="px-3 py-2 text-right">金額</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((quote) => (
                  <tr key={quote.id} className="border-t border-[var(--border)]">
                    <td className="px-3 py-2 text-sm">
                      <QuoteLink id={quote.id} quoteNo={quote.quote_no} />
                    </td>
                    <td className="px-3 py-2 text-sm text-[var(--text)]">
                      {quote.title}
                      {/* **封存的要標出來** —— 他在列表上看不到那幾張,
                          而看不到的正是最容易忘記的。 */}
                      {quote.archived ? (
                        <span className="ml-2 rounded-[var(--radius)] bg-[var(--surface-2)] px-1.5 py-0.5 text-xs text-[var(--text-3)]">
                          已封存
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <StatusBadge status={quote.status} />
                    </td>
                    <td className="tabular px-3 py-2 text-sm text-[var(--text-2)]">
                      {quote.quote_date ?? ""}
                    </td>
                    <td className="tabular px-3 py-2 text-right text-sm text-[var(--text-2)]">
                      {formatWholeMoney(quote.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 業主原話:「怕會一起改到之前的報價單」。**直接回答他這件事。** */}
          <p className="m-0 text-sm text-[var(--text)]">
            刪除之後,這 {quotes.length} 張單上的客戶名稱會固定成「{company.name}」——
            <strong>印出來一個字都不會變</strong>,只是從此不再跟著客戶檔走。
            單子本身、明細與版本史都不受影響。
          </p>

          {impact && (impact.contacts > 0 || impact.portal_users > 0) ? (
            <p className="m-0 text-sm text-[var(--text-2)]">
              另外 {impact.contacts} 位窗口、{impact.portal_users} 個客戶入口帳號
              會跟著一起消失,這一部分救不回來。
            </p>
          ) : null}

          <label className="flex items-center gap-2 text-sm text-[var(--text)]">
            <input
              type="checkbox"
              checked={checked}
              onChange={(event) => setChecked(event.target.checked)}
            />
            我已經看過上面這 {quotes.length} 張單
          </label>
        </div>
      ) : null}

      {failed ? (
        <p role="alert" className="mt-3 mb-0 text-sm text-[var(--danger)]">
          {failed}
        </p>
      ) : null}
    </Dialog>
  );
}
